import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMG } from "@/lib/site";
import { Reveal, Eyebrow } from "./Reveal";

const POINTS = [
  ["01", "The fluid", "Synovial fluid — the “joint jelly” — lubricates and cushions the space between bones."],
  ["02", "The thinning", "Its key component, hyaluronic acid, naturally declines as we age, which many people feel as stiffness."],
  ["03", "The approach", "Joint Genesis pairs Mobilee® with botanical extracts to support that fluid from the inside."],
];

export const Spotlight = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section id="science" ref={ref} className="relative bg-[#F2EDE4] py-24 sm:py-32" data-testid="spotlight-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>The science, briefly</Eyebrow>
            <h2 className="mt-5 font-display text-4xl font-bold leading-[1.02] tracking-tight text-[#091D18] sm:text-5xl">
              Stiffness often starts with a <span className="font-serif italic font-normal text-[#D96B43]">thirsty</span> joint.
            </h2>
          </Reveal>
          <div className="mt-12 space-y-8">
            {POINTS.map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.1}>
                <div className="grid grid-cols-[3rem_1fr] gap-4 border-t border-[#091D18]/15 pt-6" data-testid={`science-point-${n}`}>
                  <span className="font-mono text-sm text-[#D96B43]">{n}</span>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-[#091D18]">{t}</h3>
                    <p className="mt-2 text-base leading-relaxed text-[#091D18]/75">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:col-span-7">
          <Reveal className="col-span-2 sm:col-span-1 sm:row-span-2">
            <div className="h-[420px] overflow-hidden rounded-[2rem] sm:h-full [clip-path:polygon(0_0,100%_6%,100%_100%,0_100%)]">
              <motion.img style={{ y, scale: 1.18 }} src={IMG.couple} alt="Active couple outdoors" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="aspect-square overflow-hidden rounded-full">
              <img src={IMG.capsules} alt="Supplement capsules" className="h-full w-full object-cover transition-transform duration-700 hover:scale-110" />
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex aspect-square flex-col justify-between rounded-[2rem] bg-[#091D18] p-5 sm:p-7">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E5A93C]">Key compound</span>
              <p className="font-serif text-3xl italic leading-none text-[#F2EDE4] sm:text-4xl">Hyaluronic acid</p>
              <span className="text-xs text-[#F2EDE4]/70 sm:text-sm">Delivered via Mobilee®</span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
